import { observer } from "mobx-react-lite";
import { scaleThreshold } from "d3-scale";
import config from "../config";
import {
  sensorOverlapId,
  useMarkerPosition,
} from "../contexts/MarkerOverlapContext";
import { decimalPlaces, useLocaleContext } from "../contexts/LocaleContext";
import convert from "../convert";
import { useRootStore } from "../models/Root";
import { ISensorDisplayStream } from "../models/SensorDisplay";
import { isStreamSensor, IStreamSensor } from "../models/StreamSensor";
import { collapseDuplicateSensors } from "./duplicateSensors";
import precipDotRadiusForZoom from "./precipDotRadius";
import SensorMarker, { SensorMarkerLayer } from "./SensorMarker";
import { compareSensorMarkerOrder } from "./precipMarkerOrder";

interface StreamSensorViewProps {
  sensor: IStreamSensor;
  display: ISensorDisplayStream;
  missingMarkerSize: number;
  paintOrder: number;
  useOriginalPosition?: boolean;
}

const StreamSensorView = observer(
  ({
    sensor,
    display,
    missingMarkerSize,
    paintOrder,
    useOriginalPosition = false,
  }: StreamSensorViewProps) => {
    const { period, measure } = display;
    const {
      menus: { panel },
      sensors: { setSensor, selected },
      layers: { baseMap },
    } = useRootStore();
    const {
      units: {
        stream: { stage: toHeightUnit, discharge: toDischargeUnit },
      },
    } = useLocaleContext();

    const isSelected = selected?.sensor.id === sensor.id;
    const nudgedPosition = useMarkerPosition(
      sensorOverlapId(sensor.type, sensor.id),
      sensor.position
    );
    const position = useOriginalPosition ? sensor.position : nudgedPosition;
    const onClick = panel === "measure" ? undefined : () => setSensor(sensor);
    const data = measure === "stage" ? sensor.stage : sensor.discharge;
    const value =
      measure === "stage"
        ? sensor.stageFor(period)?.value
        : sensor.dischargeFor(period)?.value;
    const missingColor =
      config.markers.missing ||
      (baseMap.startsWith("Imagery") ? "white" : "black");

    if (data === null || data.latest === null || value === undefined) {
      return (
        <SensorMarker
          position={position}
          sensorType="stream"
          kind="missing"
          fillColor={missingColor}
          color={missingColor}
          radius={missingMarkerSize / 2}
          fillOpacity={config.markers.missing ? 1 : 0.8}
          strokeOpacity={config.markers.missing ? 1 : 0.8}
          selected={isSelected}
          paintOrder={paintOrder}
          onClick={onClick}
        />
      );
    }

    try {
      const valueForColor = convert(value)
        .from(data.unit)
        .to(measure === "stage" ? "ft" : "ft3/s");
      const toUnit = measure === "stage" ? toHeightUnit : toDischargeUnit;
      const label = convert(value)
        .from(data.unit)
        .to(toUnit)
        .toFixed(decimalPlaces(toUnit));
      const {
        stageBreakValues,
        dischargeBreakValues,
        textColors,
        backgroundColors,
      } = config.markers.stream;
      const breakValues =
        measure === "stage" ? stageBreakValues : dischargeBreakValues;

      return (
        <SensorMarker
          position={position}
          sensorType="stream"
          kind="tag"
          fillColor={scaleThreshold(breakValues, backgroundColors)(
            valueForColor
          )}
          color={scaleThreshold(breakValues, textColors)(valueForColor)}
          label={label}
          selected={isSelected}
          paintOrder={paintOrder}
          onClick={onClick}
        />
      );
    } catch {
      return (
        <SensorMarker
          position={position}
          sensorType="stream"
          kind="missing"
          fillColor={missingColor}
          color={missingColor}
          radius={missingMarkerSize / 2}
          fillOpacity={config.markers.missing ? 1 : 0.8}
          strokeOpacity={config.markers.missing ? 1 : 0.8}
          selected={isSelected}
          paintOrder={paintOrder}
          onClick={onClick}
        />
      );
    }
  }
);

const StreamSensorsLayer = ({ display }: { display: ISensorDisplayStream }) => {
  const {
    sensors: { sensorList },
    map: { zoom },
  } = useRootStore();

  const period = display.period;
  const valueForSensor = (sensor: IStreamSensor) =>
    display.measure === "stage"
      ? sensor.stageFor(period)?.value
      : sensor.dischargeFor(period)?.value;
  const compareAlert2 = (left: IStreamSensor, right: IStreamSensor) => {
    const leftHasValue = valueForSensor(left) !== undefined;
    const rightHasValue = valueForSensor(right) !== undefined;
    return (
      Number(rightHasValue) - Number(leftHasValue) ||
      left.id.localeCompare(right.id)
    );
  };
  const streamSensors = sensorList.filter(isStreamSensor);
  const duplicateSiteExpandZoomLevel =
    config.markers.precip.duplicateSiteExpandZoomLevel;
  const displaySensors =
    duplicateSiteExpandZoomLevel !== false &&
    zoom < duplicateSiteExpandZoomLevel
      ? collapseDuplicateSensors(streamSensors, compareAlert2)
      : streamSensors.map((sensor) => ({
          sensor,
          useOriginalPosition: false,
        }));
  const eligibleSensors = displaySensors.filter(
    ({ sensor }) => display.measure !== "discharge" || sensor.hasDischarge
  );
  const dotDisplayZoomLevel = config.markers.precip.dotDisplayZoomLevel;
  const missingMarkerSize =
    2 *
    (dotDisplayZoomLevel === false
      ? 7
      : precipDotRadiusForZoom(zoom, dotDisplayZoomLevel));
  const sorter = (
    a: (typeof eligibleSensors)[number],
    b: (typeof eligibleSensors)[number]
  ) => {
    const orderValue = (sensor: IStreamSensor) => {
      const data =
        display.measure === "stage" ? sensor.stage : sensor.discharge;
      const value = valueForSensor(sensor);
      let missing = true;
      if (data !== null && data.latest !== null && value !== undefined) {
        try {
          convert(value)
            .from(data.unit)
            .to(display.measure === "stage" ? "ft" : "ft3/s");
          missing = false;
        } catch {
          missing = true;
        }
      }
      return { missing, value };
    };
    return compareSensorMarkerOrder(
      orderValue(a.sensor),
      orderValue(b.sensor)
    );
  };
  return (
    <SensorMarkerLayer>
      {eligibleSensors
        .sort(sorter)
        .map(({ sensor, useOriginalPosition }, paintOrder) => (
          <StreamSensorView
            key={sensor.id}
            sensor={sensor}
            display={display}
            missingMarkerSize={missingMarkerSize}
            paintOrder={paintOrder}
            useOriginalPosition={useOriginalPosition}
          ></StreamSensorView>
        ))}
    </SensorMarkerLayer>
  );
};

export default observer(StreamSensorsLayer);
